import {Abilities, AnyAbility, ForcedSubject, subject as caslSubject} from '@casl/ability';
import {AbilityServiceSignal} from '@casl/angular';
import {
  AbilityActions,
  AbilitySubjects,
  AbilitySubjectsForAction,
  ExtractAbilities,
} from '@scalified-monorepo/shared/casl';

/**
 * Creates a set of ability-aware helpers — `can`, `canField`, `canAnyField`,
 * and `typedSubject` — bound to the same `TAbilities` context so that all
 * functions validate against the same set of valid actions and subject names
 * at compile time.
 *
 * **`can(action, subject)`**
 * Wraps `AbilityServiceSignal.can` with action-first narrowing identical to
 * `AuthorizationService.authorizeAction`.  A wrong action or action/subject
 * combination produces a direct, readable error instead of the full union dump
 * emitted by the raw `abilityService.can`.
 *
 * **`canField(action, subject, ...fields)`**
 * Field-level access check — returns `true` only when the subject has the given
 * action permitted on **all** of the specified fields.  At least one field must
 * be provided (a bare call without fields is a compile-time error; use `can`
 * instead).  Field patterns supported by CASL (`address.*`, `address.**`,
 * `street*`, etc.) are accepted.
 *
 * **`canAnyField(action, subject, ...fields)`**
 * Same as `canField` but returns `true` when the subject has the given action
 * permitted on **at least one** of the specified fields.
 *
 * **`typedSubject(subjectName, object)`**
 * Ability-aware wrapper around CASL's `subject()` helper.  Constrains
 * `subjectName` to the string literals that are actually declared in
 * `TAbilities`, so a typo like `'Role1'` is caught here — with a message like
 * `'"Role1"' is not assignable to type '"Role" | "DynamicResource" | …'` —
 * rather than propagating to the `can` call site as a confusing union error.
 *
 * @template T          - The compiled Ability type (e.g. CommonAbility)
 * @template TAbilities - Raw abilities union, auto-extracted from T
 *
 * @example
 * // In a component:
 * private readonly _ability = createTypedCan<CommonAbility>(this.abilityService);
 * protected readonly can = this._ability.can;
 * protected readonly canField = this._ability.canField;
 * protected readonly canAnyField = this._ability.canAnyField;
 * protected readonly typedSubject = this._ability.typedSubject;
 *
 * // In the template:
 * @if (can('read', 'Role')) { ... }
 * @if (can('update', typedSubject('Role', role))) { ... }
 * @if (can('update', typedSubject('Role1', role))) { } // ✗ '"Role1"' not assignable to '"Role" | …'
 * @if (can('stubAction', 'Role')) { }                  // ✗ '"Role"' not valid for 'stubAction'
 * @if (canField('read', 'Role', 'name')) { ... }
 * @if (canField('read', 'Role', 'name', 'address.*')) { ... }   // all fields must pass
 * @if (canAnyField('read', 'Role', 'name', 'address.**')) { ... } // any field must pass
 */
export function createTypedCan<T extends AnyAbility, TAbilities extends Abilities = ExtractAbilities<T>>(
  abilityService: AbilityServiceSignal<T>
) {
  type SubjectName = Extract<AbilitySubjects<TAbilities>, string>;

  function can<A extends AbilityActions<TAbilities>>(
    action: A,
    subject: AbilitySubjectsForAction<TAbilities, A>
  ): boolean {
    return (abilityService.can as unknown as (action: A, subject: AbilitySubjectsForAction<TAbilities, A>) => boolean)(
      action,
      subject
    );
  }

  function canField<A extends AbilityActions<TAbilities>>(
    action: A,
    subject: AbilitySubjectsForAction<TAbilities, A>,
    ...fields: [string, ...string[]]
  ): boolean {
    return fields.every(field =>
      (
        abilityService.can as unknown as (
          action: A,
          subject: AbilitySubjectsForAction<TAbilities, A>,
          field: string
        ) => boolean
      )(action, subject, field)
    );
  }

  function canAnyField<A extends AbilityActions<TAbilities>>(
    action: A,
    subject: AbilitySubjectsForAction<TAbilities, A>,
    ...fields: [string, ...string[]]
  ): boolean {
    return fields.some(field =>
      (
        abilityService.can as unknown as (
          action: A,
          subject: AbilitySubjectsForAction<TAbilities, A>,
          field: string
        ) => boolean
      )(action, subject, field)
    );
  }

  function typedSubject<S extends SubjectName, Obj extends object>(
    subjectName: S,
    object: Obj
  ): ForcedSubject<S> & Obj {
    return caslSubject(subjectName, {...object}) as ForcedSubject<S> & Obj;
  }

  return {can, canField, canAnyField, typedSubject};
}
